import { UUID_REGEX } from '@saas/shared';
import axios, {
  type AxiosAdapter,
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { resolveHostScope } from '@/hooks/useHostScope';

import { envKey, matchMock } from './mock/registry';
import { getAccessToken } from './access-token';
import { ALL_COMPANIES, getStoredActiveCompanyId } from './active-company';
import { getDevTenantId } from './dev-tenant-id';
import { enqueueRefresh } from './refresh-queue';
import { isTenantUpdatingError, markTenantUpdating } from './tenant-updating';

/**
 * Klient HTTP — baseURL `/api`.
 *
 * Mock adapter (TF.2): jeśli `VITE_USE_MOCK=true` lub `VITE_MOCK_<METHOD_PATH>=true`,
 * adapter próbuje znaleźć handler w `lib/mock/registry` zanim odpali HTTP.
 * Brak handlera → pass-through do real HTTP. Patrz `lib/mock/README.md`.
 *
 * F7b T7b.1: request interceptor dopina `Authorization: Bearer <access>` dla tenant scope
 * (F5 kontrakt). Response interceptor obsługuje 401 host-aware:
 *  - tenant: enqueueRefresh → retry (singleton queue, patrz `refresh-queue.ts`).
 *  - admin:  redirect `/login` (admin używa cookie, brak refresh endpointu).
 *  - public: passthrough — komponent (Register, PublicLogin) zajmie się błędem.
 */

// Rozszerzenie axios o flagę _skipAuthRefresh — gdy true, interceptor odpuszcza
// przekierowanie na /login i rzuca błąd dalej (obsługa w callsite, np. AuthContext /me).
declare module 'axios' {
  interface AxiosRequestConfig {
    _skipAuthRefresh?: boolean;
  }
}

const httpAdapter = axios.getAdapter(axios.defaults.adapter);

interface AuthAwareConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /** 503 TENANT_UPDATING dotyczy innej firmy (np. przełączenie) — bez globalnego ekranu. */
  _skipTenantUpdatingGate?: boolean;
}

const mockAwareAdapter: AxiosAdapter = async (config) => {
  if (shouldUseMock(config)) {
    const handler = matchMock(config);
    if (handler) {
      const mock = await handler(config);
      const response: AxiosResponse = {
        data: mock.data,
        status: mock.status,
        statusText: mock.statusText ?? statusTextFor(mock.status),
        headers: mock.headers ?? {},
        config,
        request: {},
      };
      if (mock.status >= 400) {
        throw new axios.AxiosError(
          `Request failed with status code ${mock.status}`,
          String(mock.status),
          config,
          response.request,
          response,
        );
      }
      return response;
    }
  }
  return httpAdapter(config);
};

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  withCredentials: true,
  adapter: mockAwareAdapter,
});

// Request: dopina Bearer dla tenant scope gdy access token jest w store.
// DEV tenant bootstrap: `getDevTenantId()` (lib/dev-tenant-id.ts) rozwiązuje uuid z 3 źródeł
// first-hit-wins: query param `?__devTenantId=<uuid>` (runtime, Register redirect + Playwright)
// → sessionStorage (persist per tab) → `VITE_DEV_TENANT_ID` (.env.local fallback, T7b.8).
// Backend middleware globalny usunięty w F15a (ADR-0009, 2026-05-11). Tenant resolution
// per-route przez `requireTenantAuth` / `requireTenantScope` w `apps/api/lib/auth/require-tenant.ts`.
// FE wstrzykuje `X-Tenant-Id` tylko w DEV (`getDevTenantId()` zwraca null w prod) — w prod
// backend rozwiązuje tenant z `Host` headera.
api.interceptors.request.use((config) => {
  const scope = resolveHostScope();
  if (scope === 'tenant') {
    const token = getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    const devTenantId = getDevTenantId();
    if (devTenantId) {
      config.headers.set('X-Tenant-Id', devTenantId);
    }
    // F9 T9.1 (slice 1D): X-Company-Id z localStorage. Backend resolwuje activeCompanyId
    // z X-Company-Id > first manager > first any. Klient steruje tylko gdy chce zmienic.
    // Tryb "Wszystkie firmy" (sentinel ALL_COMPANIES): NIE wysylamy headera wcale — backend
    // interpretuje jego brak jako unie po wszystkich firmach usera (patrz
    // resolveContractorListVisibility / resolveListCompanyScope po stronie API). Walidacja
    // UUID broni przed wyslaniem smieci gdyby localStorage mial cokolwiek inne niz sentinel
    // lub prawdziwe id firmy (np. drift po zmianie kontraktu).
    // Nie nadpisujemy headera juz jawnie ustawionego na tym requescie (np. przyszly create
    // flow z explicit per-request X-Company-Id) — interceptor dopina go TYLKO gdy go tam
    // jeszcze nie ma.
    if (!config.headers.has('X-Company-Id')) {
      const activeCompanyId = getStoredActiveCompanyId();
      if (activeCompanyId !== null && activeCompanyId !== ALL_COMPANIES) {
        if (UUID_REGEX.test(activeCompanyId)) {
          config.headers.set('X-Company-Id', activeCompanyId);
        }
      }
    }
  }
  return config;
});

// Response: host-aware 401 handling + redirect helpers (eksportowane dla testów).
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as AuthAwareConfig | undefined;
    const status = error.response?.status;

    // ADR-0113b §5: baza tenanta w trakcie aktualizacji (503 TENANT_UPDATING) — ekran „Trwa
    // aktualizacja systemu” zamiast błędu; PRZED obsługą 401 (bez wylogowania, bez odświeżania).
    if (isTenantUpdatingError(error) && resolveHostScope() === 'tenant') {
      if (!config?._skipTenantUpdatingGate) markTenantUpdating();
      return Promise.reject(error);
    }

    if (status !== 401 || !config || config._skipAuthRefresh) {
      return Promise.reject(error);
    }

    const scope = resolveHostScope();

    if (scope === 'tenant') {
      // Infinite-loop guard: original request retry-uje się raz.
      if (config._retry) {
        return Promise.reject(error);
      }
      config._retry = true;

      try {
        await enqueueRefresh(api);
      } catch (refreshErr) {
        // Odświeżenie sesji trafiło na aktualizację bazy tenanta — sesja jest ważna, nie
        // wylogowujemy (interceptor już ustawił ekran „Trwa aktualizacja systemu”).
        if (isTenantUpdatingError(refreshErr)) return Promise.reject(refreshErr);
        // Refresh padł (np. SESSION_REUSED) — wymuszony logout + redirect.
        onTenantRefreshFailed(refreshErr);
        return Promise.reject(refreshErr);
      }
      // Retry original z nowym Bearer (request interceptor znów dopnie header).
      return api.request(config);
    }

    if (scope === 'admin') {
      // Wyloguj TYLKO gdy 401 pochodzi z endpointu ADMINOWEGO (`/admin/*` lub
      // `/auth/admin/*`) — to jedyny wiarygodny sygnał wygasłej sesji admina (panel
      // stale odpytuje `/auth/admin/me` + wszystkie dane admina są pod `/admin/*`).
      // 401 z endpointu INNEGO scope'u (np. tenantowy `/system-modules/*`, `/modules/*`)
      // to pomyłkowe wywołanie spoza scope'u, NIE wygaśnięcie sesji — nie wylogowujemy,
      // tylko odrzucamy (caller pokaże błąd). Bez tego pojedyncze złe wywołanie wywalało
      // całą sesję admina (bug: dodawanie pola kartoteki → POST /system-modules/… → 401).
      const reqUrl = config.url ?? '';
      const isAdminScopedEndpoint =
        reqUrl.startsWith('/admin/') || reqUrl.startsWith('/auth/admin/');
      if (isAdminScopedEndpoint) {
        onAdminUnauthorized();
      } else if (import.meta.env.DEV) {
        console.warn(
          `[api] 401 z endpointu spoza scope'u admina (${reqUrl}) — NIE wylogowuję. ` +
            `Prawdopodobnie wywołanie tenantowego endpointu w panelu admina.`,
        );
      }
      return Promise.reject(error);
    }

    // public / unknown scope — passthrough (komponent UI sam zdecyduje co robić).
    // F12.N D1 (2026-05-15): `unknown` scope renderuje `HostMisconfigured`, więc i tak
    // nie wystrzeli żadnego requestu — passthrough jest safety net na wypadek out-of-band wywołań.
    return Promise.reject(error);
  },
);

// --- redirect helpers (testowalne stuby) ---

/**
 * Hook do overridowania w testach. W prod/dev: `window.location.href = /login`.
 * Axios interceptor żyje poza React — zmiana URL przez router-a nie jest możliwa
 * bez custom event busa. `location.href` jest prostsze i zawsze kończy sesję czysto
 * (full reload == wyczyszczony React state, invalidated query cache, świeży mount).
 */
/**
 * Pre-login pathy gdzie brak sesji jest oczekiwany — nie wolno redirectować
 * z nich do `/login`. Po kliknięciu linka z maila reset-password user trafia
 * na `/reset-password?token=...` w incognito; bez tego guarda
 * `TenantAuthProvider` próbuje `/me` → 401 → refresh fail → redirect na login
 * (bug zgłoszony 2026-05-13 na VPS tst-alx).
 */
const PRE_LOGIN_PATHS = ['/login', '/forgot-password', '/reset-password'];
function isPreLoginPath(pathname: string): boolean {
  if (PRE_LOGIN_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/accept-invite/')) return true;
  if (pathname.startsWith('/reset-password/')) return true; // admin scope wariant
  return false;
}

export const authRedirect = {
  toLogin: (reason?: string, hostScope?: string) => {
    if (typeof window === 'undefined') return;
    // Guard: pre-login pathy (login / forgot / reset / accept-invite) — brak sesji
    // jest tam oczekiwany. Zapobiega zarówno pętli redirect (T7b.8 preview) jak
    // i kradzieży UX flow'u resetu hasła (incognito → 401 /me → redirect).
    if (isPreLoginPath(window.location.pathname)) return;
    const params = new URLSearchParams();
    if (reason) params.set('reason', reason);
    if (hostScope) params.set('hostScope', hostScope);
    const qs = params.toString();
    window.location.href = qs ? `/login?${qs}` : '/login';
  },
};

function onAdminUnauthorized(): void {
  authRedirect.toLogin(undefined, 'admin');
}

function onTenantRefreshFailed(err: unknown): void {
  const axiosErr = err as AxiosError<{ error?: string }>;
  const code = axiosErr.response?.data?.error;
  const reason = code === 'SESSION_REUSED' ? 'session-reused' : 'session-expired';
  authRedirect.toLogin(reason);
}

function shouldUseMock(config: InternalAxiosRequestConfig): boolean {
  const env = (import.meta.env ?? {}) as Record<string, string | boolean | undefined>;
  if (env.VITE_USE_MOCK === 'true' || env.VITE_USE_MOCK === true) return true;
  const key = envKey(config);
  return env[key] === 'true' || env[key] === true;
}

function statusTextFor(status: number): string {
  if (status >= 200 && status < 300) return 'OK';
  if (status >= 400 && status < 500) return 'Client Error';
  if (status >= 500) return 'Server Error';
  return '';
}
